import { z } from 'zod';

// Зеркалит Prisma-enum'ы из libs/prisma/schema.prisma. Расширение — синхронно сюда И в schema.
// BROADCAST с витрины снят (2026-10-02), остаётся как значение Postgres-enum'а.
export const PromotionChannelSchema = z.enum([
  'SOCIAL',
  'MORNING_DIGEST',
  'BROADCAST',
  'ADS',
  /** Внутренние рекомендации на экране «Я» (issue #175, пакет 3). */
  'APP_RECOMMENDATIONS',
]);
export type PromotionChannel = z.infer<typeof PromotionChannelSchema>;

export const PromotionTaskStatusSchema = z.enum(['NEW', 'DONE', 'CANCELLED']);
export type PromotionTaskStatus = z.infer<typeof PromotionTaskStatusSchema>;

// Стоимость продвижения в соцсетях (issue #82). Единственный источник истины, без env.
export const PROMOTION_SOCIAL_COST = 200;

// Стоимость продвижения в утренней рассылке (issue #82).
export const PROMOTION_MORNING_DIGEST_COST = 100;

/**
 * Реклама события: 25 000 показов через внешний сервис, задача менеджеру —
 * как у соцсетей. Цена равна энергии, которую даёт Pro (STATUS_ENERGY_AMOUNT):
 * покупка Pro = ровно одна такая кампания плюс перки статуса.
 */
export const PROMOTION_ADS_COST = 2500;
export const PROMOTION_ADS_IMPRESSIONS = 25_000;

/** Энергия за Pro и за Gold — один раз на человека за каждый статус. */
export const STATUS_ENERGY_AMOUNT = 2500;

/** Каналы, которые обрабатывает менеджер из очереди `/admin/promotion-tasks`. */
export const MANAGER_PROMOTION_CHANNELS = ['SOCIAL', 'ADS'] as const;
export type ManagerPromotionChannel = (typeof MANAGER_PROMOTION_CHANNELS)[number];

export const SlotSourceSchema = z.enum(['PROMOTION', 'ADMIN']);
export type SlotSource = z.infer<typeof SlotSourceSchema>;

// --- mini-app: статус продвижения события ---
export const PromotionChannelOptionDto = z.object({
  channel: PromotionChannelSchema,
  cost: z.number().int(),
  enabled: z.boolean(),
});
export type PromotionChannelOptionDto = z.infer<typeof PromotionChannelOptionDto>;

export const EventPromotionStatusDto = z.object({
  /**
   * Доступная энергия: coinBalance + сумма отрицательных PENDING-холдов (не сырой
   * ledger-баланс). По ней экран рисует «осталось N» и считается `affordable`.
   */
  balance: z.number().int(),
  channels: z.array(PromotionChannelOptionDto),
  /** Активная (NEW) заявка соцсетей. Оставлено ради старых бандлов клиента — новое читает `activeTasks`. */
  activeTask: z
    .object({
      id: z.uuid(),
      channel: PromotionChannelSchema,
      status: PromotionTaskStatusSchema,
      createdAt: z.iso.datetime(),
    })
    .nullable(),
  /** Все активные (NEW) заявки менеджерских каналов — у соцсетей и рекламы своя. */
  activeTasks: z
    .array(
      z.object({
        id: z.uuid(),
        channel: PromotionChannelSchema,
        status: PromotionTaskStatusSchema,
        createdAt: z.iso.datetime(),
      }),
    )
    .optional(),
  morningDigest: z
    .object({
      slotsPerDay: z.number().int(),
      availability: z.array(z.object({ date: z.string(), freeSlots: z.number().int() })),
      myBookings: z.array(z.object({ slotId: z.uuid(), date: z.string(), taskId: z.uuid() })),
    })
    .nullable(),
});
export type EventPromotionStatusDto = z.infer<typeof EventPromotionStatusDto>;

export const CreatePromotionInput = z.object({
  channel: PromotionChannelSchema,
  organizerNote: z.string().max(500).trim().optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(), // обязателен для MORNING_DIGEST (валидируется в сервисе)
});
export type CreatePromotionInput = z.infer<typeof CreatePromotionInput>;

// --- admin: очередь задач ---
export const PromotionTaskListQuery = z.object({
  status: PromotionTaskStatusSchema.default('NEW'),
  cityId: z.uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type PromotionTaskListQuery = z.infer<typeof PromotionTaskListQuery>;

export const PromotionTaskDto = z.object({
  id: z.uuid(),
  channel: PromotionChannelSchema,
  status: PromotionTaskStatusSchema,
  organizerNote: z.string().nullable(),
  resultNote: z.string().nullable(),
  energySpent: z.number().int(),
  createdAt: z.iso.datetime(),
  handledAt: z.iso.datetime().nullable(),
  event: z.object({
    id: z.uuid(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    cityId: z.uuid(),
  }),
  requestedBy: z.object({
    id: z.uuid(),
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    energyAvailable: z.number().int(),
  }),
});
export type PromotionTaskDto = z.infer<typeof PromotionTaskDto>;

export const PromotionTaskListResponse = z.object({
  items: z.array(PromotionTaskDto),
  nextCursor: z.string().nullable(),
});
export type PromotionTaskListResponse = z.infer<typeof PromotionTaskListResponse>;

export const PromotionTaskDoneInput = z
  .object({ resultNote: z.string().max(1000).trim().optional() })
  .default({});
export type PromotionTaskDoneInput = z.infer<typeof PromotionTaskDoneInput>;

export const PromotionTaskCancelInput = z.object({
  resultNote: z.string().min(1).max(1000).trim(),
});
export type PromotionTaskCancelInput = z.infer<typeof PromotionTaskCancelInput>;

export const CancelPromotionInput = z.object({ slotId: z.uuid() });
export type CancelPromotionInput = z.infer<typeof CancelPromotionInput>;

// --- admin: слоты редактора рассылки ---
export const MorningSlotDto = z.object({
  id: z.uuid(),
  date: z.string(),
  source: SlotSourceSchema,
  isPaid: z.boolean(),
  event: z.object({ id: z.uuid(), title: z.string(), startsAt: z.iso.datetime() }),
  createdByName: z.string().nullable(),
});
export type MorningSlotDto = z.infer<typeof MorningSlotDto>;

export const MorningSlotCalendarResponse = z.object({
  slotsPerDay: z.number().int(),
  days: z.array(z.object({ date: z.string(), freeSlots: z.number().int(), slots: z.array(MorningSlotDto) })),
});
export type MorningSlotCalendarResponse = z.infer<typeof MorningSlotCalendarResponse>;

export const MorningSlotCalendarQuery = z.object({
  cityId: z.uuid(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type MorningSlotCalendarQuery = z.infer<typeof MorningSlotCalendarQuery>;

export const CreateMorningSlotInput = z.object({
  cityId: z.uuid(),
  eventId: z.uuid(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type CreateMorningSlotInput = z.infer<typeof CreateMorningSlotInput>;
